import { ModelTopology } from './schema';

function normalizeTimingLookupKey(value) {
  if (typeof value !== 'string') return null;
  const normalizedValue = value.trim();
  return normalizedValue || null;
}

function normalizeAliasTimingProfile(timing, owner) {
  if (timing == null) return null;
  if (typeof timing !== 'object' || Array.isArray(timing)) {
    throw new Error(`[ModelRepository] ${owner} timing must be an object.`);
  }

  const baseSeconds = Number.parseFloat(timing.baseSeconds ?? timing.base);
  if (!Number.isFinite(baseSeconds) || baseSeconds <= 0) {
    throw new Error(`[ModelRepository] ${owner} timing is missing positive baseSeconds.`);
  }

  return {
    source: 'curated-existing-config',
    ...timing,
    base: baseSeconds,
    baseSeconds,
  };
}

export function resolveModelTimingAliasTopology(model, aliasKey, {
  explicitTopology = null,
  candidateTopologies = [],
}) {
  if (explicitTopology) {
    if (model.routes?.[explicitTopology]) return explicitTopology;
    throw new Error(
      `[ModelRepository] Model "${model.id}" timing alias "${aliasKey}" references missing route "${explicitTopology}".`,
    );
  }

  const matchingTopologies = candidateTopologies.filter((topology) => model.routes?.[topology]);
  if (matchingTopologies.length === 1) return matchingTopologies[0];
  if (matchingTopologies.length > 1) {
    throw new Error(
      `[ModelRepository] Model "${model.id}" timing alias "${aliasKey}" matches multiple routes. Declare an explicit topology.`,
    );
  }

  throw new Error(
    `[ModelRepository] Model "${model.id}" timing alias "${aliasKey}" does not match a catalog route.`,
  );
}

function addTimingAlias(aliases, key, model, topologyOptions) {
  const aliasKey = normalizeTimingLookupKey(key);
  if (!aliasKey) return;

  aliases.set(aliasKey, {
    model,
    topology: resolveModelTimingAliasTopology(model, aliasKey, topologyOptions),
    timing: normalizeAliasTimingProfile(
      topologyOptions?.timing,
      `Model "${model.id}" timing alias "${aliasKey}"`,
    ),
  });
}

function routeTimingFor(model, topology) {
  return model.routes?.[topology]?.timing || model.timing || null;
}

export function buildTimingAliasMap(models = []) {
  const aliases = new Map();

  for (const model of models) {
    addTimingAlias(aliases, model.ui?.image?.editKey, model, {
      explicitTopology: model.ui?.image?.editTopology,
      timing: model.ui?.image?.editTiming,
      candidateTopologies: [ModelTopology.IMAGE_TO_IMAGE, ModelTopology.MASK_IMAGE_TO_IMAGE],
    });

    addTimingAlias(aliases, model.ui?.generativeFill?.loadingModelKey, model, {
      explicitTopology: model.ui?.generativeFill?.loadingTopology,
      timing: model.ui?.generativeFill?.loadingTiming,
      candidateTopologies: [ModelTopology.IMAGE_TO_IMAGE, ModelTopology.MASK_IMAGE_TO_IMAGE],
    });

    addTimingAlias(aliases, model.ui?.smartEdit?.loadingModelKey, model, {
      explicitTopology: model.ui?.smartEdit?.loadingTopology,
      timing: model.ui?.smartEdit?.loadingTiming,
      candidateTopologies: [ModelTopology.VIDEO_TO_VIDEO],
    });
  }

  return aliases;
}

export function lookupTimingProfileForKey({
  modelByIdMap,
  timingAliasByKey,
  modelKey,
}) {
  const normalizedModelKey = normalizeTimingLookupKey(modelKey);
  if (!normalizedModelKey) return null;

  const model = modelByIdMap?.get?.(normalizedModelKey) || null;
  if (model) return model.timing || null;

  const alias = timingAliasByKey?.get?.(normalizedModelKey);
  if (!alias) return null;

  return alias.timing || routeTimingFor(alias.model, alias.topology);
}
